import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { MessageSquareText } from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useFacebookPagesQuery } from "@/modules/facebook/presentation/api/queries";
import { QueryState } from "@/shared/ui/QueryState";
import { useTemplatesQuery } from "../api/queries";
import type { TemplateKind } from "../api/types";
import { CurrencyRatesCard } from "../ui/CurrencyRatesCard";
import { StatusNotificationSettingsCard } from "../ui/StatusNotificationSettingsCard";
import { TemplateEditorCard } from "../ui/TemplateEditorCard";
import { UtilityTemplateSettingsCard } from "../ui/UtilityTemplateSettingsCard";

const TAB_LABELS: Record<TemplateKind, string> = {
  order_reply_post: "ຕອບກັບໃນໂພສ",
  order_reply_live: "ຕອບກັບໃນໄລຟ໌",
  cf_order_unavailable: "CF ບໍ່ສຳເລັດ",
  pos_order_created: "ອໍເດີ POS ໃໝ່",
  payment_reminder: "ທວງຍອດ",
  payment_received: "ຢືນຢັນຊຳລະ",
  qr_delivery: "ຂໍ້ຄວາມ QR",
  status_update: "ອັບເດດສະຖານະ",
};

const TAB_DESC: Record<TemplateKind, string> = {
  order_reply_post: "ສົ່ງອັດຕະໂນມັດເຂົ້າ Messenger ຫຼັງລູກຄ້າຄອມເມັນສັ່ງສິນຄ້າໃນໂພສ",
  order_reply_live: "ສົ່ງອັດຕະໂນມັດເຂົ້າ Messenger ຫຼັງລູກຄ້າຄອມເມັນສັ່ງສິນຄ້າໃນໄລຟ໌",
  cf_order_unavailable: "ສົ່ງຫາລູກຄ້າເມື່ອ CF ບໍ່ສາມາດຮັບອໍເດີໄດ້",
  pos_order_created: "ສົ່ງອັດຕະໂນມັດຫຼັງພະນັກງານສ້າງອໍເດີຈາກ POS",
  payment_reminder:
    "ໃຊ້ເປັນຂໍ້ຄວາມຫຼັກຂອງ Page ນີ້ ທັງການທວງຍອດອັດຕະໂນມັດທຸກ 24 ຊົ່ວໂມງ ແລະປຸ່ມ “ສົ່ງຕອນນີ້” ໃນ Admin; ນອກກອບ 24 ຊົ່ວໂມງ ຂໍ້ຄວາມນີ້ຈະຖືກໃສ່ໃນ Meta Utility {{2}}",
  payment_received: "ສົ່ງເຂົ້າ Messenger ອັດຕະໂນມັດ ຫຼັງລະບົບຢືນຢັນວ່າຮັບການຊຳລະເຕັມຈຳນວນແລ້ວ",
  qr_delivery:
    "ສົ່ງພ້ອມຮູບ QR ຫຼັງລູກຄ້າກົດຮັບ QR ໃນ Messenger — ປິດໄດ້ໂດຍຮູບ QR ຍັງຖືກສົ່ງຕາມປົກກະຕິ",
  status_update: "ສົ່ງສະຖານະຫຼັກຂອງຄຳສັ່ງຊື້ພ້ອມປຸ່ມເບິ່ງລາຍລະອຽດ",
};

const KIND_ORDER: TemplateKind[] = [
  "order_reply_post",
  "order_reply_live",
  "cf_order_unavailable",
  "pos_order_created",
  "payment_reminder",
  "payment_received",
  "qr_delivery",
  "status_update",
];

function resolveTemplateTab(value: unknown): TemplateKind {
  return KIND_ORDER.includes(value as TemplateKind)
    ? (value as TemplateKind)
    : "order_reply_post";
}

export function MessageTemplatesPage() {
  const search = useSearch({ from: "/app/message-templates" }) as {
    tab?: string;
  };
  const nav = useNavigate({ from: "/app/message-templates" });
  const activeTab = resolveTemplateTab(search.tab);
  const pagesQuery = useFacebookPagesQuery();
  const [pageId, setPageId] = useState<string | null>(null);

  const activePages =
    pagesQuery.data?.pages.filter((p) => p.status !== "disconnected") ?? [];

  useEffect(() => {
    if (!pageId && activePages.length > 0) {
      const connected = activePages.find((p) => p.status === "connected");
      setPageId(connected?.id ?? activePages[0]?.id ?? null);
    }
  }, [pageId, activePages]);

  const templatesQuery = useTemplatesQuery(pageId);
  const selectedPage = activePages.find((p) => p.id === pageId);

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2">
              <MessageSquareText className="size-5 text-primary" aria-hidden />
              <h1 className="font-semibold text-2xl tracking-tight">
                ການຕັ້ງຄ່າຂໍ້ຄວາມ
              </h1>
            </div>
            <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
              ກຳນົດຂໍ້ຄວາມ Messenger ຕໍ່ Page — ແຍກຕາມໂພສ, ໄລຟ໌, ທວງຍອດ, ຢືນຢັນການຊຳລະ
              ແລະ ຂໍ້ຄວາມ QR
            </p>
          </div>
        </div>

        <div className="mb-6 rounded-xl border bg-card p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 flex-1 space-y-1 lg:max-w-md">
              <span className="text-muted-foreground text-xs">
                Facebook Page
              </span>
              <QueryState result={pagesQuery} variant="inline">
                <Select
                  value={pageId ?? ""}
                  onValueChange={(v) => setPageId(v)}
                >
                  <SelectTrigger className="w-full min-w-0 overflow-hidden">
                    <SelectValue
                      className="min-w-0 flex-1 truncate"
                      placeholder="ເລືອກ Page"
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {activePages.length === 0 ? (
                      <SelectItem value="__none__" disabled>
                        ຍັງບໍ່ມີ Page ທີ່ເຊື່ອມ
                      </SelectItem>
                    ) : (
                      activePages.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.pageName}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </QueryState>
            </div>
            {selectedPage ? (
              <p
                className="min-w-0 max-w-full truncate text-muted-foreground text-sm lg:max-w-xl"
                title={selectedPage.pageName}
              >
                ກຳລັງຕັ້ງຄ່າ:{" "}
                <span className="font-medium text-foreground">
                  {selectedPage.pageName}
                </span>
              </p>
            ) : null}
          </div>
        </div>

        {!pageId ? (
          <div className="rounded-xl border border-dashed bg-muted/30 px-6 py-12 text-center text-muted-foreground text-sm">
            ເລືອກ Facebook Page ເພື່ອແກ້ໄຂ template
          </div>
        ) : (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
            <section className="min-w-0 space-y-4">
              <QueryState result={templatesQuery}>
                <Tabs
                  value={activeTab}
                  onValueChange={(value) =>
                    nav({
                      search: (prev) => ({
                        ...prev,
                        tab: resolveTemplateTab(value),
                      }),
                      replace: true,
                    })
                  }
                  className="w-full"
                >
                  <TabsList className="grid h-auto w-full grid-cols-2 gap-1 p-1 sm:grid-cols-4 lg:grid-cols-8">
                    {KIND_ORDER.map((k) => (
                      <TabsTrigger
                        key={k}
                        value={k}
                        className="h-auto whitespace-normal px-2 py-2 text-center text-xs sm:text-sm"
                      >
                        {TAB_LABELS[k]}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {KIND_ORDER.map((k) => {
                    const template = templatesQuery.data?.templates.find(
                      (t) => t.kind === k,
                    );
                    if (!template) return null;
                    return (
                      <TabsContent key={k} value={k} className="mt-4">
                        {k === "payment_reminder" &&
                        templatesQuery.data?.utilitySettings ? (
                          <UtilityTemplateSettingsCard
                            pageId={pageId}
                            settings={templatesQuery.data.utilitySettings}
                          />
                        ) : null}
                        {k === "status_update" &&
                        templatesQuery.data?.notificationSettings ? (
                          <div className="mb-4">
                            <StatusNotificationSettingsCard
                              pageId={pageId}
                              settings={
                                templatesQuery.data.notificationSettings
                              }
                              statusEventTemplates={
                                templatesQuery.data.statusEventTemplates ?? []
                              }
                              statusUpdateFallback={template.content}
                              variables={templatesQuery.data.variables ?? []}
                            />
                          </div>
                        ) : null}
                        <TemplateEditorCard
                          pageId={pageId}
                          template={template}
                          variables={templatesQuery.data?.variables ?? []}
                          title={TAB_LABELS[k]}
                          description={TAB_DESC[k]}
                        />
                      </TabsContent>
                    );
                  })}
                </Tabs>
              </QueryState>
            </section>

            <aside className="min-w-0">
              <CurrencyRatesCard compact />
            </aside>
          </div>
        )}
      </Main>
    </>
  );
}
